"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import ServiceloanCards from "../individual-loan/page";
import ServicebuisnessCards from "../business-loans/page";
import ServicedevelopmentCards from "../development-financing/page";

const services = [
  {
    title: "Micro Loans",
    image: "/projects/microf.png",
    description:
      "Tailored small-scale financing solutions designed to empower entrepreneurs and small businesses.",
    lists: [
      "Working capital loans",
      "Equipment financing",
      "Trade credit facilities",
    ],
    details:
      "Our micro loans feature flexible repayment terms, competitive interest rates, and quick approval processes to support your business growth and operational needs.",
  },
  {
    title: "Agricultural Financing",
    image: "/projects/agriculturalfinancing1.png",
    description:
      "Specialized financial products for farmers and agribusinesses to boost productivity.",
    lists: [
      "Seasonal crop financing",
      "Livestock loans",
      "Irrigation equipment loans",
    ],
    details:
      "We offer customized agricultural loans with crop-cycle aligned repayment schedules, input financing, and technical support to enhance farm productivity and sustainability.",
  },
];

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const text = "Explore our loan products & financing Services";

export default function ServiceCards() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  return (
    <div className="mt-30 mb-20 space-y-16 pt-28 overflow-x-hidden sm:pt-10 md:pt-6 lg:pt-0">
      {/* Animated Hero Section */}
      <div className="relative w-full overflow-hidden py-24 md:py-32" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}>
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <svg
            className="h-full w-full"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0 L100,0 L100,100 L0,100 Z"
              fill="none"
              stroke={colors.white}
              strokeWidth="0.5"
              strokeDasharray="2 2"
            />
          </svg>
        </div>

        {/* Content container */}
        <div className="container relative mx-auto px-6">
          <h1 className="text-center font-orbitron text-4xl font-bold text-white sm:text-5xl md:text-6xl lg:text-7xl">
            <span className="relative inline-block">
              {text.substring(0, currentIndex)}
              <motion.span
                className="absolute -right-3 top-0 h-12 w-1 bg-white"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scaleY: [1, 1.2, 1],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 0.8,
                  ease: "easeInOut",
                }}
              />
            </span>
          </h1>

          {/* Decorative elements */}
          <div className="mt-12 flex justify-center space-x-4">
            {[1, 2, 3].map((item) => (
              <motion.div
                key={item}
                className="h-2 rounded-full bg-white/30"
                initial={{ width: 0 }}
                animate={{ width: item % 2 === 0 ? "40px" : "20px" }}
                transition={{
                  duration: 0.6,
                  delay: item * 0.2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              />
            ))}
          </div>
        </div>

        {/* Wave divider at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-12 overflow-hidden">
          <svg
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="h-full w-full"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              opacity=".25"
              fill={colors.white}
            ></path>
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              opacity=".5"
              fill={colors.white}
            ></path>
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              fill={colors.white}
            ></path>
          </svg>
        </div>
      </div>

      {/* Projects Section */}
      <ServiceloanCards />
      <ServicebuisnessCards />
      
      <div
        className="mt-20 px-4 lg:mx-auto lg:max-w-[1500px]"
        id="special-loans"
      >
      </div>
    </div>
  );
}